import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Loading, PageHeader, Pager } from '../App';
import { EmptyState, ErrorState } from '../components/States';
import {
  adminApi,
  date,
  dateTime,
  classifyError,
  errorMessage,
  humanise,
  money,
  statusVariant,
  type AdminProperty,
  type ApiError,
  type PagedResult,
} from '../api/admin';

type Decision = 'approve' | 'reject' | 'correction' | 'suspend' | 'reinstate' | 'archive';

const DECISION_LABELS: Record<Decision, string> = {
  approve: 'Approve listing',
  reject: 'Reject listing',
  correction: 'Request corrections',
  suspend: 'Suspend listing',
  reinstate: 'Reinstate listing',
  archive: 'Archive listing',
};

/** Decisions that must carry a reason the owner will read. */
const REASON_REQUIRED: Decision[] = ['reject', 'correction', 'suspend'];

export function ModerationPage() {
  const [params, setParams] = useSearchParams();

  const [data, setData] = useState<PagedResult<AdminProperty> | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Separate from `error`, which carries approve/reject failures: a list that never
  // loaded must not render as an empty table, or an outage reads as a quiet day.
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pending, setPending] = useState<{ property: AdminProperty; decision: Decision } | null>(null);

  const status = params.get('status') ?? '';
  const category = params.get('category') ?? '';
  const keyword = params.get('keyword') ?? '';
  const reported = params.get('reported') === 'true';
  const page = Number(params.get('page') ?? 1);

  const load = useCallback(async () => {
    setError(null);

    try {
      setData(
        await adminApi.properties({
          status: status || undefined,
          category: category || undefined,
          keyword: keyword || undefined,
          reported: reported || undefined,
          page,
          pageSize: 20,
        }),
      );
    } catch (err) {
      setLoadError(classifyError(err));
    }
  }, [status, category, keyword, reported, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const setFilter = (key: string, value?: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('page');
    setParams(next);
  };

  return (
    <>
      <PageHeader
        title="Listing moderation"
        description="Approve, reject or suspend listings. Every decision is recorded with its reason and shown to the owner."
      />

      <Alert message={error} />
      <Alert message={success} variant="success" />

      <div className="admin-card mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-3">
            <label className="form-label small mb-1" htmlFor="mod-status">
              Status
            </label>
            <select
              id="mod-status"
              className="form-select form-select-sm"
              value={status}
              onChange={(e) => setFilter('status', e.target.value)}
            >
              <option value="">All</option>
              {['PendingReview', 'Active', 'ExpiringSoon', 'Draft', 'PendingPayment', 'Rejected', 'Expired', 'Suspended', 'Archived'].map(
                (value) => (
                  <option key={value} value={value}>
                    {humanise(value)}
                  </option>
                ),
              )}
            </select>
          </div>

          <div className="col-12 col-md-3">
            <label className="form-label small mb-1" htmlFor="mod-category">
              Category
            </label>
            <select
              id="mod-category"
              className="form-select form-select-sm"
              value={category}
              onChange={(e) => setFilter('category', e.target.value)}
            >
              <option value="">All</option>
              <option value="PgHostel">PG &amp; Hostel</option>
              <option value="HomeRental">Home rental</option>
              <option value="Hotel">Hotel</option>
            </select>
          </div>

          <div className="col-12 col-md-3">
            <label className="form-label small mb-1" htmlFor="mod-keyword">
              Search
            </label>
            <input
              id="mod-keyword"
              className="form-control form-control-sm"
              defaultValue={keyword}
              placeholder="Title, locality or business"
              onKeyDown={(e) => {
                if (e.key === 'Enter') setFilter('keyword', (e.target as HTMLInputElement).value);
              }}
              onBlur={(e) => setFilter('keyword', e.target.value)}
            />
          </div>

          <div className="col-12 col-md-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="mod-reported"
                checked={reported}
                onChange={(e) => setFilter('reported', e.target.checked ? 'true' : undefined)}
              />
              <label className="form-check-label small" htmlFor="mod-reported">
                Only reported listings
              </label>
            </div>
          </div>
        </div>
      </div>

      {loadError ? (
        <ErrorState error={loadError} onRetry={load} />
      ) : !data ? (
        <Loading rows={6} />
      ) : data.items.length === 0 ? (
        <EmptyState
          title="No listings match these filters"
          description="Clear or widen the filters above to see more."
        />
      ) : (
        <>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th scope="col">Listing</th>
                  <th scope="col">Owner</th>
                  <th scope="col">Status</th>
                  <th scope="col">Billing</th>
                  <th scope="col">Submitted</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((property) => (
                  <tr key={property.id}>
                    <td>
                      <div className="fw-semibold">{property.title}</div>
                      <div className="text-muted small">
                        {humanise(property.category)} · {property.locality} · {property.photoCount} photos
                        {property.priceFrom !== undefined && property.priceFrom !== null && (
                          <> · from {money(property.priceFrom)}</>
                        )}
                      </div>
                      {property.openReportCount > 0 && (
                        <span className="badge bg-danger mt-1">{property.openReportCount} open report(s)</span>
                      )}
                      {property.statusReason && (
                        <div className="text-muted small fst-italic mt-1">{property.statusReason}</div>
                      )}
                    </td>

                    <td>
                      <div>{property.ownerName}</div>
                      <div className="text-muted small">{property.businessName}</div>
                    </td>

                    <td>
                      <span className={`badge bg-${statusVariant(property.status)}`}>{humanise(property.status)}</span>
                      {property.expiresAtUtc && (
                        <div className="text-muted small mt-1">Until {date(property.expiresAtUtc)}</div>
                      )}
                    </td>

                    <td>
                      {property.hasValidBilling ? (
                        <span className="badge bg-success">Paid</span>
                      ) : (
                        <span className="badge bg-secondary">Unpaid</span>
                      )}
                    </td>

                    <td className="text-muted small text-nowrap">{dateTime(property.submittedAtUtc)}</td>

                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {property.status === 'PendingReview' && (
                          <>
                            <button
                              type="button"
                              className="btn btn-sm btn-success"
                              onClick={() => setPending({ property, decision: 'approve' })}
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => setPending({ property, decision: 'reject' })}
                            >
                              Reject
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => setPending({ property, decision: 'correction' })}
                            >
                              Ask for changes
                            </button>
                          </>
                        )}

                        {(property.status === 'Active' || property.status === 'ExpiringSoon') && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => setPending({ property, decision: 'suspend' })}
                          >
                            Suspend
                          </button>
                        )}

                        {property.status === 'Suspended' && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-success"
                            onClick={() => setPending({ property, decision: 'reinstate' })}
                          >
                            Reinstate
                          </button>
                        )}

                        {property.status !== 'Archived' && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setPending({ property, decision: 'archive' })}
                          >
                            Archive
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pager
            result={data}
            onPage={(next) => {
              const query = new URLSearchParams(params);
              query.set('page', String(next));
              setParams(query);
            }}
          />
        </>
      )}

      {pending && (
        <DecisionDialog
          property={pending.property}
          decision={pending.decision}
          onCancel={() => setPending(null)}
          onDone={async (message) => {
            setPending(null);
            setSuccess(message);
            await load();
          }}
          onError={setError}
        />
      )}
    </>
  );
}

function DecisionDialog({
  property,
  decision,
  onCancel,
  onDone,
  onError,
}: {
  property: AdminProperty;
  decision: Decision;
  onCancel: () => void;
  onDone: (message: string) => Promise<void>;
  onError: (message: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const reasonRequired = REASON_REQUIRED.includes(decision);

  const submit = async () => {
    if (reasonRequired && reason.trim().length < 5) {
      onError('Give the owner a reason of at least five characters.');
      return;
    }

    setBusy(true);

    try {
      switch (decision) {
        case 'approve':
          await adminApi.approve(property.id, reason.trim() || undefined);
          break;
        case 'reject':
          await adminApi.reject(property.id, reason.trim(), internalNotes.trim() || undefined);
          break;
        case 'correction':
          await adminApi.requestCorrection(property.id, reason.trim());
          break;
        case 'suspend':
          await adminApi.suspendListing(property.id, reason.trim());
          break;
        case 'reinstate':
          await adminApi.reinstate(property.id, reason.trim() || undefined);
          break;
        case 'archive':
          await adminApi.archiveListing(property.id, reason.trim() || undefined);
          break;
      }

      await onDone(`${DECISION_LABELS[decision]}: done for ${property.title}.`);
    } catch (err) {
      onError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop show" />
      <div className="modal d-block" role="dialog" aria-modal="true" aria-labelledby="decision-title">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title h5" id="decision-title">
                {DECISION_LABELS[decision]}
              </h2>
            </div>

            <div className="modal-body">
              <p className="mb-3">
                <strong>{property.title}</strong>
                <br />
                <span className="text-muted small">
                  {property.businessName} · {property.ownerEmail}
                </span>
              </p>

              {decision === 'approve' && !property.hasValidBilling && (
                <div className="alert alert-warning py-2 px-3 small">
                  This listing has no paid, unexpired billing record. The server will refuse the approval — approval
                  cannot be used to publish an unpaid listing.
                </div>
              )}

              <div className="mb-3">
                <label className="form-label" htmlFor="decision-reason">
                  Reason {reasonRequired ? '(required, shown to the owner)' : '(optional, shown to the owner)'}
                </label>
                <textarea
                  id="decision-reason"
                  className="form-control"
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={1000}
                  placeholder={
                    decision === 'reject'
                      ? 'For example: the photographs do not match the stated address.'
                      : undefined
                  }
                />
              </div>

              {decision === 'reject' && (
                <div className="mb-0">
                  <label className="form-label" htmlFor="decision-notes">
                    Internal note (never shown to the owner)
                  </label>
                  <textarea
                    id="decision-notes"
                    className="form-control"
                    rows={2}
                    value={internalNotes}
                    onChange={(e) => setInternalNotes(e.target.value)}
                    maxLength={2000}
                  />
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={busy}>
                Cancel
              </button>
              <button
                type="button"
                className={`btn ${decision === 'approve' || decision === 'reinstate' ? 'btn-success' : 'btn-danger'}`}
                onClick={submit}
                disabled={busy}
              >
                {busy ? 'Working…' : DECISION_LABELS[decision]}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
